<template>
  <div
    class="col project-card"
    :class="[$config.project, { 'dark': dark }]"
  >
    <nuxt-link
      :to="`/projects/${project.slug}/`"
      class="card-body"
    >
      <div class="image-holder">
        <img loading="lazy" :src="`${$config.imagesUrl}/projects/${project.image}`">
        <div class="badge-holder" :class="{ 'extend': project.technology }">
          <div class="badge">
            <Badge v-for="(badge, id) in project.badges" :key="id" :name="badge" />
          </div>
          <div v-if="project.technology" class="technology">
            <components :is="require(`../assets/svg/tech-responsive-${project.technology}.svg?vue-component`)" />
          </div>
        </div>
      </div>
      <div class="card-footer">
        <component :is="heading" class="title">
          {{ project.title }}
        </component>
        <p v-if="project.subtype" class="project-type">
          Type: <span class="highlight">{{ project.subtype }} project</span>
        </p>
        <p class="duration">
          Duration: <span class="highlight">{{ duration }}</span>
        </p>
        <p v-if="project.status.key && project.status.key !== 'not-started'" class="status">
          Status: <span :class="[[project.status.key]]">{{ project.status.name }} <CheckIcon v-if="project.status.key === 'done'" /></span>
        </p>
      </div>
    </nuxt-link>
  </div>
</template>

<script>
import CheckIcon from '../assets/svg/icon-check-projects.svg?vue-component';
import Badge from './Badge';

export default {
  components: {
    CheckIcon, Badge,
  },
  props: {
    project: { type: Object, required: true },
    heading: { type: String, default: 'h5', required: false },
    dark: Boolean,
  },
  computed: {
    duration() {
      if (this.project.duration === 0) {
        return '< 1 hour';
      }

      return `${this.project.duration} hours`;
    },
  },
};
</script>

<style lang="scss">
.project-card {
  margin-bottom: var(--spacing-20);

  &.site {
    .card-body {
      min-height: 333px;

      .card-footer {
        padding: var(--spacing-20) var(--spacing-24);

        .title {
          font-size: 18px;
          line-height: 130%;
        }
      }
    }
  }

  &.dark {
    .card-body {
      background: var(--main-dark-light-2);

      .card-footer {
        .title {
          color: var(--main-light);
        }

        p {
          color: var(--main-light-shade-4);

          .highlight {
            color: var(--main-light);
          }
        }
      }
    }
  }

  .card-body {
    display: block;
    text-decoration: none;
    color: var(--main-dark-shade-0);
    transition: transform var(--duration) ease;
    border-radius: 10px;
    height: 100%;
    box-shadow: 0 3px 8px rgba(16, 20, 28, 0.15);
    background: var(--main-light);

    &:hover {
      transform: scale(1.03);

      .title {
        color: var(--accent);
      }
    }

    .image-holder {
      height: 193px;
      border-top-left-radius: 10px;
      border-top-right-radius: 10px;
      overflow: hidden;
      position: relative;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .badge-holder {
        position: absolute;
        top: 12px;
        right: 12px;

        &.extend {
          display: flex;
          width: calc(100% - 24px);
          justify-content: space-between;
        }

        .badge {
          display: flex;

          .library-badge {
            margin-right: var(--spacing-4);

            &:last-of-type {
              margin-right: 0;
            }
          }
        }

        .technology {
          svg {
            width: 28px;
          }
        }
      }
    }

    .card-footer {
      padding: var(--spacing-12) var(--spacing-16) var(--spacing-16);

      .title {
        font-weight: 600;
        font-size: 16px;
        line-height: 162%;
        margin-bottom: var(--spacing-16);
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
      }

      p {
        margin: 0;
        font-weight: 500;
        line-height: 164%;
        color: var(--main-dark-light-3);
        font-size: 14px;

        &:last-of-type {
          margin-bottom: 0;
        }
      }

      .duration,
      .project-type {
        margin-bottom: var(--spacing-4);
      }

      .project-type {
        .highlight {
          text-transform: capitalize;
        }
      }

      .highlight {
        font-weight: 700;
        color: var(--main-dark);
      }

      .status {
        svg {
          width: 12px;
          height: 9px;
        }

        span {
          font-weight: 700;

          &.in-progress {
            color: var(--accent-shade-1);
          }

          &.done {
            color: var(--main-green-shade-1);
          }
        }
      }
    }
  }
}

@include bp($bp-mobile) {
  .project-card {
    &:last-of-type {
      margin-bottom: 0;
    }
  }
}

</style>
